import { motion } from "framer-motion";
import {
  CheckCircle2,
  XCircle,
  Loader2,
  Circle,
  SkipForward,
  UserCheck,
  RotateCcw,
  Bot,
  FileOutput,
  AlertTriangle,
  Play,
} from "lucide-react";
import type { ExecStep, ExecStepStatus, HiveExecution } from "@/types";
import { useExecution } from "@/store/executionStore";
import { STEP_STATUS_META, EXEC_STATUS_META, Pill } from "@/components/hive/shared";
import { KIND_META } from "@/data/mockWorkflowNodes";

function StepDot({ status }: { status: ExecStepStatus }) {
  const props = { size: 16 };
  switch (status) {
    case "completed": return <CheckCircle2 {...props} className="text-emerald-500" />;
    case "failed": return <XCircle {...props} className="text-rose-500" />;
    case "running": return <Loader2 {...props} className="animate-spin text-cyan-400" />;
    case "retrying": return <RotateCcw {...props} className="animate-spin text-amber-500" />;
    case "skipped": return <SkipForward {...props} className="text-app-2" />;
    case "waiting-approval": return <UserCheck {...props} className="text-fuchsia-400" />;
    default: return <Circle {...props} className="text-app-2/40" />;
  }
}

export function ExecutionTimeline({ exec }: { exec: HiveExecution }) {
  const selectedStepId = useExecution((s) => s.selectedStepId);
  const selectStep = useExecution((s) => s.selectStep);
  const retryStep = useExecution((s) => s.retryStep);
  const rerunFromStep = useExecution((s) => s.rerunFromStep);
  const m = EXEC_STATUS_META[exec.status];

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2.5 border-b border-app px-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-app">{exec.name}</div>
          <div className="truncate text-[11px] text-app-2">{exec.triggerEvent}</div>
        </div>
        <Pill color={m.color} dot={exec.status === "running"} pulse={exec.status === "running"}>{m.label}</Pill>
      </div>

      <div className="flex-1 overflow-y-auto no-scrollbar p-4">
        <div className="relative">
          {/* vertical rail */}
          <div className="absolute bottom-2 left-[10px] top-2 w-px bg-app" />
          <div className="space-y-1">
            {exec.steps.map((step, i) => (
              <StepCard
                key={step.id}
                step={step}
                last={i === exec.steps.length - 1}
                expanded={step.id === selectedStepId}
                onToggle={() => selectStep(step.id === selectedStepId ? null : step.id)}
                onRetry={() => retryStep(exec.id, step.id)}
                onRerun={() => rerunFromStep(exec.id, step.id)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StepCard({
  step,
  expanded,
  onToggle,
  onRetry,
  onRerun,
}: {
  step: ExecStep;
  last: boolean;
  expanded: boolean;
  onToggle: () => void;
  onRetry: () => void;
  onRerun: () => void;
}) {
  const sm = STEP_STATUS_META[step.status];
  const kc = KIND_META[step.kind].color;
  const active = step.status === "running" || step.status === "retrying";

  return (
    <div className="relative pl-7">
      <div className="absolute left-0 top-1.5 grid h-[21px] w-[21px] place-items-center rounded-full bg-[hsl(var(--bg-1))]">
        <StepDot status={step.status} />
      </div>
      <motion.button
        layout
        onClick={onToggle}
        className="w-full rounded-lg border px-3 py-2 text-left transition"
        style={{
          borderColor: active ? `${sm.color}66` : "hsl(var(--border))",
          background: active ? `${sm.color}0f` : "hsl(var(--surface) / 0.4)",
        }}
      >
        <div className="flex items-center gap-2">
          <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: kc }} />
          <span className="flex-1 truncate text-xs font-medium text-app">{step.label}</span>
          {step.retries ? <span className="flex items-center gap-0.5 text-[10px] text-amber-500"><RotateCcw size={10} /> {step.retries}</span> : null}
          {step.cost ? <span className="text-[10px] tabular-nums text-app-2">${step.cost.toFixed(2)}</span> : null}
          <span className="text-[10px] text-app-2 tabular-nums">{step.at}</span>
          <span className="text-[10px] font-medium capitalize" style={{ color: sm.color }}>{step.status.replace("-", " ")}</span>
        </div>

        <div className="mt-0.5 flex items-center gap-2 pl-3.5 text-[10px] text-app-2">
          {step.agent && <span className="flex items-center gap-0.5"><Bot size={10} /> {step.agent}</span>}
          <span>· {step.duration}</span>
          {step.log && !expanded && <span className="truncate">· {step.log}</span>}
        </div>

        {expanded && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-2 space-y-1.5 overflow-hidden pl-3.5">
            {step.log && (
              <div className="rounded-md border border-app bg-surface-2/50 px-2 py-1.5 font-mono text-[10px] text-app-1">{step.log}</div>
            )}
            {step.io && (
              <div className="flex items-center gap-1.5 text-[10px] text-app-1"><FileOutput size={11} className="text-app-2" /> {step.io}</div>
            )}
            {step.error && (
              <div className="flex items-start gap-1.5 rounded-md border border-rose-500/30 bg-rose-500/5 px-2 py-1.5 text-[10px] text-rose-500">
                <AlertTriangle size={11} className="mt-0.5 shrink-0" /> {step.error}
              </div>
            )}
            {(step.status === "failed" || step.status === "completed") && (
              <div className="flex items-center gap-1.5 pt-0.5">
                {step.status === "failed" && (
                  <button onClick={(e) => { e.stopPropagation(); onRetry(); }} className="flex items-center gap-1 rounded-md bg-amber-500/15 px-2 py-1 text-[10px] font-medium text-amber-500 transition hover:bg-amber-500/25"><RotateCcw size={10} /> Retry step</button>
                )}
                <button onClick={(e) => { e.stopPropagation(); onRerun(); }} className="flex items-center gap-1 rounded-md border border-app bg-surface-2/60 px-2 py-1 text-[10px] font-medium text-app-1 transition hover:border-app-strong"><Play size={10} /> Re-run from here</button>
              </div>
            )}
          </motion.div>
        )}
      </motion.button>
    </div>
  );
}
